import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAgentPermissions } from "../../context/AgentPermissionsContext";
import { useGuidedTips } from "../../context/GuidedTipsContext";
import { tipForPath } from "./tipsContent";

/**
 * Optional, non-blocking guidance bubble for the current screen
 * (UX-GUIDED-TIPS-001). Fixed to a bottom corner so it never covers the
 * page's controls, takes no focus, is dismissible, and never gates any
 * operation. Renders nothing when tips are off, dismissed, or there is no
 * tip the user is allowed to see for this screen. The card can be minimised
 * to a small chip (session-only, per screen) so it never hides page content.
 */
export default function GuidedTip() {
  const { pathname } = useLocation();
  const { user } = useAuth() || {};
  const { can: canAgent } = useAgentPermissions() || {};
  const tips = useGuidedTips();
  const [collapsedRoute, setCollapsedRoute] = useState(null);
  if (!tips || !tips.enabled) return null;

  const role = user?.role;
  const isAdmin = role === "admin" || role === "owner";
  const tip = tipForPath(pathname, { isAdmin, canAgent, role });
  if (!tip || tips.isDismissed(tip.route)) return null;

  if (collapsedRoute === tip.route) {
    return (
      <button
        type="button"
        dir="rtl"
        data-guided-tip-chip={tip.route}
        onClick={() => setCollapsedRoute(null)}
        aria-label="הצג טיפ מודרך"
        className="fixed bottom-4 left-4 z-20 rounded-full border border-blue-200 bg-white shadow-lg px-3 py-1 text-xs text-blue-700 hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
      >
        💡 טיפ
      </button>
    );
  }

  return (
    <aside
      dir="rtl"
      aria-label="טיפ מודרך"
      data-guided-tip={tip.route}
      className="fixed bottom-4 left-4 z-20 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-blue-200 bg-white shadow-lg p-3 text-xs leading-relaxed text-gray-700"
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <p className="font-semibold text-sm text-gray-900">{tip.what}</p>
        <div className="shrink-0 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setCollapsedRoute(tip.route)}
          aria-label="מזער טיפ"
          className="text-gray-400 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
        >
          –
        </button>
        <button
          type="button"
          onClick={() => tips.dismiss(tip.route)}
          aria-label="סגור טיפ"
          className="text-gray-400 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
        >
          ✕
        </button>
        </div>
      </div>
      <p className="mb-1">{tip.does}</p>
      <p className="mb-1">
        <span className="font-medium">פעולה עיקרית: </span>
        {tip.primary}
      </p>
      <p className="mb-1">
        <span className="font-medium">שימו לב: </span>
        {tip.risk}
      </p>
      <p className="mb-2">
        <span className="font-medium">הצעד הבא: </span>
        {tip.next}
      </p>
      <div className="flex items-center justify-between gap-2 text-[11px]">
        {tip.helpTopic ? (
          <Link to={`/help?topic=${encodeURIComponent(tip.helpTopic)}`} className="text-blue-600 hover:underline">
            למידע נוסף
          </Link>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={() => tips.setEnabled(false)}
          className="text-gray-500 hover:text-gray-800 underline"
        >
          כבה טיפים
        </button>
      </div>
    </aside>
  );
}
